import { Box, Card, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import i18n from '../../../i18n';
import TableComponent, { HeadCell } from '../../../component/table/table.component';
import { formatEUR } from '../../../constants/tool.fn';
import { useCallback, useEffect } from 'react';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import RefundDataStore, { PageType } from './refund-data-state';
import moment from 'moment';
import dataAnalysisStore from '../data-analysis-state';
import { ErrorMessageBox } from '../data-analysis.page';
import { useTranslation } from 'react-i18next';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import InfoIcon from '@mui/icons-material/Info';
import { DataExportIconButton } from '../../../component/button/data-export-icon-button.component';
import GlobalNotification from '../../../component/common/snackbar';
import './scss/refund-data.style.scss';
import {
    formatRefundCheckId,
    formatRefundOrderHeadId,
    getRefundDishReasonDisplayText,
    type RefundDishRow,
} from './refund-reason-display';
import OrderDetailDialog from '../order-data/order-detail-dialog';
import { Order } from '../../../models/order.entity';

const RefundDishDataPage = observer(({ pageType }: Props) => {
    useTranslation()
    // 本地 MobX store，管理退菜数据和排序状态
    const refundDataStore = useLocalObservable(() => new RefundDataStore());
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    // 当前时间段筛选
    const startDate = dataAnalysisStore.startDate
    const endDate = dataAnalysisStore.endDate

    useEffect(() => {
        refundDataStore.fetchData(stringId, startDate, endDate, pageType)
    }, [stringId, startDate, endDate, pageType])

    const preCheckExport = useCallback((): boolean => {
        if (!refundDataStore.data?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return false;
        }
        return true;
    }, [refundDataStore.data?.length]);

    const handleExportRefund = useCallback(() => {
        refundDataStore.downloadExcel(pageType);
    }, [refundDataStore, pageType]);

    /** 列表行是否可打开订单详情（隧道需 order_head_id；云端需 server_id） */
    const rowCanOpenOrderDetail = useCallback(
        (item: RefundDishRow) => {
            if (pageType === 'INSTANT') {
                const id = item.order_head_id;
                if (id == null) return false;
                const n = Number(id);
                return !Number.isNaN(n) && n > 0;
            }
            const sid = item.server_id;
            if (sid == null || sid === '') return false;
            return String(sid).trim().length > 0;
        },
        [pageType],
    );

    /** 打开订单详情弹窗并拉取详情 */
    const handleOpenOrderDetailDialog = useCallback(
        (value: RefundDishRow) => {
            if (!rowCanOpenOrderDetail(value)) {
                return;
            }
            if (pageType === 'INSTANT') {
                const stub: Order = { order_head_id: value.order_head_id };
                refundDataStore.setOrderDetailData(stub);
            } else {
                refundDataStore.setOrderDetailData({ server_id: String(value.server_id) });
            }
            refundDataStore.setOpenOrderDetail(true);
            void refundDataStore.fetchDetailData(pageType);
        },
        [pageType, refundDataStore, rowCanOpenOrderDetail],
    );

    /**
     * 获取当前列的排序状态
     * @param key 列字段名
     * @returns 0=不排序 1=降序 2=升序
     */
    const getSortStatus = (key: string) => {
        const sort = refundDataStore.sortKeyValue;
        if (key === sort?.key) {
            return sort.value === 'ASC' ? 2 : 1
        }
        return 0
    }

    /** 表头列配置（按文案长度设 minWidth，窄屏横向滚动、不挤压列宽） */
    const headCellList: HeadCell[] = [
        {
            id: 'item_name1',
            label: i18n.t('business_analysis_page_dish_name_one'),
            minWidth: '180px',
        },
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_head_id'),
            minWidth: '140px',
            renderFunction: (item) => (
                <div>{formatRefundOrderHeadId(item as RefundDishRow)}</div>
            ),
        },
        {
            id: 'check_id',
            label: i18n.t('business_analysis_page_order_account'),
            minWidth: '120px',
            renderFunction: (item) => (
                <div>{formatRefundCheckId(item as RefundDishRow)}</div>
            ),
        },
        {
            id: 'table_name',
            label: i18n.t('business_analysis_page_table_name'),
            minWidth: '140px',
        },
        {
            id: 'product_price',
            label: i18n.t('general_text_price'),
            minWidth: '120px',
            sortStatus: getSortStatus('product_price'),
            renderFunction: (item) => (
                <div>
                    {formatEUR(+(item?.product_price || 0))}
                </div>
            )
        },
        {
            id: 'quantity',
            label: i18n.t('business_analysis_page_refund_quantity'),
            minWidth: '100px',
            sortStatus: getSortStatus('quantity'),
        },
        {
            id: 'return_time',
            label: i18n.t('business_analysis_page_refund_time'),
            minWidth: '180px',
            renderFunction: (item) => (
                <div>
                    {moment(item.return_time).format('YYYY-MM-DD HH:mm:ss')}
                </div>
            )
        },
        {
            id: 'return_reason',
            label: i18n.t('business_analysis_page_refund_reason'),
            minWidth: '200px',
            // 退菜原因：`return_reason` + 括号类型（由 `is_history` 与 `status` 决定，见 refund-reason-display）
            renderFunction: (item) => (
                <div>{getRefundDishReasonDisplayText(item as RefundDishRow)}</div>
            ),
        },
        {
            id: 'order_employee_id',
            // CLOUD 模式标题"操作人员"，INSTANT 模式标题"退菜操作账号"
            label: pageType === 'CLOUD'
                ? i18n.t('business_analysis_page_operator_person')
                : i18n.t('business_analysis_page_refund_operating_account'),
            minWidth: '160px',
            sortStatus: -1
        }
    ]

    /**
     * 列头点击排序处理
     * @param headCell 被点击的列头
     */
    const handleSort = (headCell: HeadCell) => {
        let sortStatus: 'ASC' | 'DESC' | '' = ''
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC'
                break;
            case 1:
                sortStatus = 'ASC'
                break;
            case 2:
                sortStatus = ''
                break;
            default:
                sortStatus = ''
                break;
        }
        const value = {
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined
        }
        refundDataStore.setSortValue(value)
    }

    /**
     * INSTANT 模式标题区域：Info 图标 + 橙色"当前退菜详情"文字
     */
    const renderInstantTitle = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: '16px' }}>
            <InfoIcon sx={{ color: 'var(--func-sp1)', fontSize: 40 }} />
            <Typography
                variant="h4"
                sx={{ color: 'var(--func-sp1)', fontWeight: 500, lineHeight: '32px' }}
            >
                {i18n.t('business_analysis_page_immediate_return_details')}
            </Typography>
        </Box>
    )

    /** CLOUD 模式标题区域：标题 + 操作区（下载/日期范围） */
    const renderCloudTitle = () => (
        <Box className='refund-data-cloud-top-bar'>
            <Typography variant="h3" sx={{ color: 'var(--text-dark)', lineHeight: '32px' }}>
                {i18n.t('business_analysis_page_refund_info')}
            </Typography>
            <Box className='refund-data-cloud-top-actions'>
                <DataExportIconButton
                    title={i18n.t('tunnel_management_page_export')}
                    disabled={refundDataStore.loading}
                    preCheck={preCheckExport}
                    onExport={handleExportRefund}
                />
                <PeriodDateFilter
                    startDate={dataAnalysisStore.startDate}
                    endDate={dataAnalysisStore.endDate}
                    pickerValue={dataAnalysisStore.pickerValue}
                    onDateTimeChange={dataAnalysisStore.handleDateTime}
                />
            </Box>
        </Box>
    )

    return (
        refundDataStore.errorMessage
            ? <ErrorMessageBox errorMessage={refundDataStore.errorMessage} />
            :
            <>
                <Box className={pageType === 'CLOUD' ? 'refund-data-page-cloud' : 'refund-data-page-instant'}>
                    <Card
                        className={pageType === 'CLOUD' ? 'refund-data-card-cloud' : 'refund-data-card-instant'}
                        sx={{
                            margin: pageType === 'CLOUD' ? '16px 0 0' : '16px 0 24px',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            padding: pageType === 'CLOUD' ? '24px 16px 16px' : '16px',
                        }}
                    >
                        {/* INSTANT 模式标题：Info 图标 + 橙色标题 */}
                        {pageType === 'INSTANT' && renderInstantTitle()}
                        {/* CLOUD 模式标题：加粗标题 + 搜索/多选操作区 */}
                        {pageType === 'CLOUD' && renderCloudTitle()}
                        <TableComponent
                            extraClass={pageType === 'CLOUD' ? 'refund-data-cloud-table' : 'refund-data-instant-table'}
                            // 分页
                            isPagination
                            allCount={refundDataStore.data?.length}
                            // 列表项
                            headCellList={headCellList}
                            // 列表数据
                            list={refundDataStore.data}
                            // 是否显示复选框 0 不显示 1显示  2 显示但是不显示删除提示框
                            showChecked={0}
                            // 是否显示加载框 0不开启
                            isTableLoading={refundDataStore.loading}
                            // 排序方法
                            handleSort={handleSort}
                            onRowClick={(row) => {
                                if (rowCanOpenOrderDetail(row as RefundDishRow)) {
                                    handleOpenOrderDetailDialog(row as RefundDishRow);
                                }
                            }}
                        />
                    </Card>
                </Box>
                <OrderDetailDialog
                    handleClose={() => refundDataStore.setOpenOrderDetail(false)}
                    open={refundDataStore.isOpenOrderDetail}
                    order={refundDataStore.orderDetailSummary}
                    list={refundDataStore.orderDetailDish}
                    isTableLoading={refundDataStore.detailLoading}
                />
            </>

    )
})

export default RefundDishDataPage

type Props = {
    // 页面类型：退菜详情(CLOUD) 或 即时退菜详情(INSTANT)
    pageType: PageType
}
